import React from 'react';

export const Documentation = () => {
  return (
    <section id="docs" className="documentation">
      <div className="container">
        <div className="section-header">
          <h2>API Reference</h2>
          <p>Complete documentation for the LSend framework</p>
        </div>

        <div className="docs-grid">
          {/* EmailBuilder Methods */}
          <div className="docs-card">
            <h3>EmailBuilder Methods</h3>
            <div className="docs-table-wrapper">
              <table className="docs-table">
                <thead>
                  <tr>
                    <th>Method</th>
                    <th>Parameters</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td data-label="Method"><code>sender()</code></td>
                    <td data-label="Parameters"><code>String</code></td>
                    <td data-label="Description">Set sender (supports "Name &lt;email&gt;" format)</td>
                  </tr>
                  <tr>
                    <td data-label="Method"><code>to()</code></td>
                    <td data-label="Parameters"><code>String / List&lt;String&gt;</code></td>
                    <td data-label="Description">Add recipient(s)</td>
                  </tr>
                  <tr>
                    <td data-label="Method"><code>cc()</code></td>
                    <td data-label="Parameters"><code>String / List&lt;String&gt;</code></td>
                    <td data-label="Description">Add CC recipient(s)</td>
                  </tr>
                  <tr>
                    <td data-label="Method"><code>bcc()</code></td>
                    <td data-label="Parameters"><code>String / List&lt;String&gt;</code></td>
                    <td data-label="Description">Add BCC recipient(s)</td>
                  </tr>
                  <tr>
                    <td data-label="Method"><code>replyTo()</code></td>
                    <td data-label="Parameters"><code>String</code></td>
                    <td data-label="Description">Set reply-to address</td>
                  </tr>
                  <tr>
                    <td data-label="Method"><code>subject()</code></td>
                    <td data-label="Parameters"><code>String</code></td>
                    <td data-label="Description">Set email subject</td>
                  </tr>
                  <tr>
                    <td data-label="Method"><code>html()</code></td>
                    <td data-label="Parameters"><code>String</code></td>
                    <td data-label="Description">Set HTML body content</td>
                  </tr>
                  <tr>
                    <td data-label="Method"><code>text()</code></td>
                    <td data-label="Parameters"><code>String</code></td>
                    <td data-label="Description">Set plain text body</td>
                  </tr>
                  <tr>
                    <td data-label="Method"><code>staticResource()</code></td>
                    <td data-label="Parameters"><code>String, Map&lt;String,String&gt;</code></td>
                    <td data-label="Description">Use HTML template with {'{{variable}}'} replacement</td>
                  </tr>
                  <tr>
                    <td data-label="Method"><code>template()</code></td>
                    <td data-label="Parameters"><code>String</code></td>
                    <td data-label="Description">Use Salesforce template by developer name</td>
                  </tr>
                  <tr>
                    <td data-label="Method"><code>attach()</code></td>
                    <td data-label="Parameters"><code>String, Blob</code></td>
                    <td data-label="Description">Add attachment from Blob</td>
                  </tr>
                  <tr>
                    <td data-label="Method"><code>attachContentVersion()</code></td>
                    <td data-label="Parameters"><code>Id</code></td>
                    <td data-label="Description">Add attachment from ContentVersion</td>
                  </tr>
                  <tr>
                    <td data-label="Method"><code>attachContentVersions()</code></td>
                    <td data-label="Parameters"><code>List&lt;Id&gt;</code></td>
                    <td data-label="Description">Add multiple attachments from CVs (Optimized)</td>
                  </tr>
                  <tr>
                    <td data-label="Method"><code>saveActivity()</code></td>
                    <td data-label="Parameters"><code>Boolean</code></td>
                    <td data-label="Description">Save email as activity on record</td>
                  </tr>
                  <tr>
                    <td data-label="Method"><code>send()</code></td>
                    <td data-label="Parameters">-</td>
                    <td data-label="Description">Send the email, returns EmailResult</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Classes Overview */}
          <div className="docs-card">
            <h3>Classes Overview</h3>
            <div className="class-list">
              <div className="class-item">
                <div className="class-name">LSend</div>
                <div className="class-desc">Main entry point. Use <code>LSend.emails()</code> to get an EmailBuilder.</div>
              </div>
              <div className="class-item">
                <div className="class-name">EmailBuilder</div>
                <div className="class-desc">Fluent builder for configuring and sending emails.</div>
              </div>
              <div className="class-item">
                <div className="class-name">EmailAttachment</div>
                <div className="class-desc">Helper class for creating attachments from Blob or ContentVersion.</div>
              </div>
              <div className="class-item">
                <div className="class-name">EmailResult</div>
                <div className="class-desc">Result wrapper with <code>isSuccess()</code>, <code>getError()</code>, and
                  <code>getId()</code>.
                </div>
              </div>
              <div className="class-item">
                <div className="class-name">EmailException</div>
                <div className="class-desc">Custom exception for validation and sending errors.</div>
              </div>
            </div>
          </div>

          {/* Important Notes */}
          <div className="docs-card warning-card">
            <h3>Important Notes</h3>
            <ul className="notes-list">
              <li>
                <strong>UTF-8 Required:</strong> Static Resource HTML templates must be encoded in UTF-8 or you will
                receive: <code>System.StringException: BLOB is not a valid UTF-8 string</code>
              </li>
              <li>
                <strong>Daily Limit:</strong> Salesforce allows 5,000 emails per day (varies by edition)
              </li>
              <li>
                <strong>Recipients Limit:</strong> Maximum 150 recipients per email
              </li>
              <li>
                <strong>Attachment Size:</strong> Maximum 25 MB total per email
              </li>
            </ul>
          </div>

          {/* Installation */}
          <div className="docs-card">
            <h3>Installation</h3>
            <div className="install-section" style={{ marginBottom: '32px' }}>
              <h4 style={{ fontSize: '16px', color: 'var(--text-primary)', marginBottom: '16px' }}>Option 1: Package (Recommended)</h4>
              <p className="install-intro" style={{ marginBottom: '16px' }}>Install the library directly into your Salesforce environment.</p>
              <a
                href="https://login.salesforce.com/packaging/installPackage.apexp?p0=04tfj000000DtOT"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary-sm"
              >
                Install Package via Salesforce
              </a>
            </div>

            <div className="install-section">
              <h4 style={{ fontSize: '16px', color: 'var(--text-primary)', marginBottom: '16px' }}>Option 2: Source Code</h4>
              <p className="install-intro">Deploy the following classes to your Salesforce org:</p>
              <div className="install-files">
                <span className="file-badge">LSend.cls</span>
                <span className="file-badge">EmailBuilder.cls</span>
                <span className="file-badge">EmailAttachment.cls</span>
                <span className="file-badge">EmailResult.cls</span>
                <span className="file-badge">EmailException.cls</span>
              </div>
              <div className="install-command">
                <code>sf project deploy start --source-dir force-app/main/default/classes</code>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
